<template>
  <b-card
    class="mb-0"
  >
    <div class="mb-2">
      <b-row>
        <b-col
          cols="12"
          md="4"
        >
          <div class="d-flex align-items-center justify-content-end">
            <h4 class="card-title mb-0 mr-2 pt-50">
              Candidates</h4>
            <b-form-group class="w-75 mb-md-0">
              <v-select
                id="status_id"
                v-model="status_id"
                :options="status"
                :reduce="item => item.id"
                :clearable="true"
                placeholder="Select Status"
                @input="statusChanged"
              />
            </b-form-group>
          </div>
        </b-col>
        <b-col
          cols="12"
          md="8 ml-auto"
        >
          <div class="d-flex align-items-center justify-content-end">
            <b-input-group>
              <b-form-input
                v-model="searchQuery"
                placeholder="Search"
                @keyup.enter="updateSearchQuery"
              />
              <b-input-group-append>
                <b-button
                  variant="outline-primary"
                  @click="updateSearchQuery"
                >
                  <feather-icon
                    icon="SearchIcon"
                    size="14"
                  />
                </b-button>
              </b-input-group-append>
            </b-input-group>
            <b-button
              variant="primary"
              class="ml-2 min-w-160"
              :to="{ name: 'human-resources-advanced-search'}"
            >
              Advance Search
            </b-button>
            <b-button
              variant="primary"
              class="ml-2 min-w-90"
              :to="{ name: 'job-human-resources-existing-candidates', params: { job_id: job_id}, query: {name: title}}"
            >
              Apply
            </b-button>
            <b-button
              variant="outline-primary"
              class="ml-2 min-w-80"
              @click="back"
            >
              Back
            </b-button>
          </div>
        </b-col>
      </b-row>

    </div>

    <vue-good-table
      :columns="columns"
      :rows="candidates"
      :rtl="direction"
      :search-options="{
        enabled: true,
        externalQuery: searchTerm }"
      :pagination-options="{
        enabled: true,
        perPage:pageLength
      }"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >

        <!-- Column: Schedule Interview -->
        <span v-if="props.column.field === 'cellRendererScheduleInterview'">
          <b-button
            variant="primary"
            :to="{ name: 'job-human-resources-job-interview', params: { job_candidate: props.row.id}, query: { name: props.row.prospect_name } }"
            class="px-1 py-25"
          >
            Schedule Interview
          </b-button>
        </span>

        <!-- Column: Schedule Interview -->
        <span v-if="props.column.field === 'cellRendererInterviewDate'">
          <span
            v-for="option in props.row.interview_dates"
            :key="option"
            class="d-block"
          >
            <span
              :key="option"
              v-text="option"
            />
          </span>
        </span>

        <!-- Column: Status -->
        <span v-else-if="props.column.field === 'status'">
          <b-badge :variant="statusVariant(props.row.status)">
            {{ props.row.status }}
          </b-badge>
        </span>

        <!-- Column: Action -->
        <span
          v-else-if="props.column.field === 'action'"
          class="btn-action"
        >
          <span>
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none pl-0"
              no-caret
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle mr-25"
                />
              </template>
              <b-dropdown-item
                v-if="props.row.status.toLowerCase() === 'active'"
                :to="{ name: 'human-resources-candidates-edit', params: { id: props.row.id } }"
              >
                <feather-icon icon="Edit2Icon" />
                <span class="ml-50">Edit</span>
              </b-dropdown-item>
              <b-dropdown-item @click="confirmDeleteRecord(props.row)">
                <feather-icon icon="TrashIcon" />
                <span class="ml-50">Delete</span>
              </b-dropdown-item>
            </b-dropdown>
            <b-link
              v-if="props.row.status.toLowerCase() === 'active'"
              :to="{ name: 'human-resources-candidates-view', params: { id: props.row.id } }"
            >
              <b-badge
                pill
                variant="light-primary"
              ><feather-icon icon="EyeIcon" />
              </b-badge>
            </b-link>
            <b-link
              v-if="props.row.status.toLowerCase() !== 'active'"
              :to="{ name: 'human-resources-candidates-inactive-candidate-view', params: { id: props.row.id } }"
            >
              <b-badge
                pill
                variant="light-primary"
              ><feather-icon icon="EyeIcon" />
              </b-badge>
            </b-link>
          </span>
        </span>

        <!-- Column: Common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- pagination -->
      <template
        slot="pagination-bottom"
      >
        <div class="d-flex justify-content-end flex-wrap">
          <div>
            <b-pagination
              v-model="currentPage"
              :total-rows="count"
              first-number
              last-number
              align="right"
              :per-page="1"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BBadge, BPagination, BFormGroup, BInputGroup, BFormInput, BInputGroupAppend, BDropdown, BDropdownItem, BButton, BLink,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { VueGoodTable } from 'vue-good-table'
import store from '@/store/index'
import Candidates from '@/js/candidates'
import LIST from '@/js/lists'

export default {
  name: 'CandidatesList',
  components: {
    BCard,
    BRow,
    BCol,
    VueGoodTable,
    BBadge,
    BPagination,
    BFormGroup,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BLink,
    BDropdown,
    BDropdownItem,

    vSelect,
  },
  data() {
    return {
      status_id: null,
      searchQuery: '',
      pageLength: 10,
      dir: false,
      status: [],
      candidates: [],
      count: 1,
      title: '',
      columns: [
        {
          label: '#',
          field: 'index',
          width: '100px',
        },
        {
          label: 'Name',
          field: 'prospect_name',
          sortable: false,
        },
        {
          label: 'Supplier Name',
          field: 'supplier_name',
          sortable: false,
        },
        {
          label: 'Submitted Date',
          field: 'submitted_date',
          width: '120px',
          sortable: false,
        },
        {
          label: 'Last Activity',
          field: 'last_activity',
          sortable: false,
        },
        {
          label: 'Last Activity Date',
          field: 'last_activity_date',
          width: '125px',
          sortable: false,
        },
        {
          label: 'Current Stage',
          field: 'current_activity',
          sortable: false,
        },
        {
          label: 'Status',
          field: 'status',
          width: '115px',
          sortable: false,
        },
        {
          label: 'Action',
          field: 'action',
          width: '100px',
          sortable: false,
        },
      ],
      rows: [],
      searchTerm: '',
    }
  },
  computed: {
    currentPage: {
      get() {
        return this.$store.state.query.innerpage
      },
      set(val) {
        this.$store.state.query.innerpage = val
        Candidates.getCandidates(this, this.$route.params.job_id, val)
      },
    },
    statusVariant() {
      const statusColor = {
        /* eslint-disable key-spacing */
        Active      : 'light-success',
        Inactive    : 'light-danger',
        Terminated  : 'light-info',
        /* eslint-enable key-spacing */
      }

      return status => statusColor[status]
    },
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true
        return this.dir
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false
      return this.dir
    },
  },
  watch: {
    '$store.state.lists.hrLists': {
      immediate: true,
      handler() {
        LIST.getProspectStatusFullList(this)
      },
    },
  },
  created() {
    this.job_id = this.$route.params.job_id
    this.status_id = this.$store.state.query.status_id
    this.searchQuery = this.$store.state.query.innerQuery
    this.count = this.$store.state.query.innerpage
    Candidates.getCandidates(this, this.$route.params.job_id, this.currentPage)
    LIST.getProspectStatusFullList(this)
  },
  methods: {
    confirmDeleteRecord(obj) {
      this.$swal({
        title: 'Are you sure?',
        text: `you wan't to delete this ${obj.prospect_name}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-primary ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          Candidates.delete(this, obj.id)
        }
      })
    },
    candidateDeleted() {
      Candidates.getCandidates(this, this.$route.params.job_id, this.currentPage)
      this.$swal({
        icon: 'success',
        title: 'Candidate Deleted!',
        text: 'Your Candidate has been deleted',
      })
    },
    candidateNotDeleted() {
      this.$swal({
        icon: 'warning',
        title: 'Candidate Not Deleted!',
        text: 'Your Candidate has been not deleted',
      })
    },
    updateSearchQuery() {
      this.$store.state.query.query = this.searchQuery
      this.currentPage = 1
    },
    statusChanged() {
      this.$store.state.query.status_id = this.status_id
      this.currentPage = 1
    },
    setStatus(status) {
      this.status = status
    },
    setCandidates(data) {
      this.candidates = data.job_candidates
      this.title = data.title
      this.count = data.count
    },
    back() {
      this.$router.go(-1)
    },
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
